// GameController (IMPLEMENTATION §2.1, §0.1): owns the Simulation and is the only code that moves it.
//   dispatch(cmd)  applies at dispatch time (phase 0 of the next tick, logged by the sim) and fans the
//                  command's events out in the same frame — no input lag;
//   step()         one tick (scenario commands due first), events fanned out;
//   frame(now)     the GameLoop's ticks, the 10 Hz UI snapshot, then the renderer's frame(alpha);
//   advance(n, {silent})  n ticks now; silent = no cosmetic events reach render / audio (the renderer
//                  rebuilds its views from the state afterwards: the state alone is the truth);
//   replace()      a loaded save / loadSim / reset: new sim, views rebuilt, caches dropped.
// Sinks: the renderer and audio get cosmetic events (never when silent) — a dispatch fans out inside the
// caller's task, so an input handler's command plays its sound in the gesture (M9: the iOS cold-open pop);
// the UI gets the snapshot; the `__gm` buffer (hooks builds) records every event for tests. Counters prove
// the silent contract (e2e spy).
// A throw inside a frame (the sim tick or the view sync) HALTS the game: the loop stops, `onError` shows the
// ErrorScreen, and the app stops saving (the last good save and its .bak stay intact).
// M12: `observers` (telemetry sink, trace recorder) see every new Simulation, every command and every tick's
// events — silent advances included (telemetry is not cosmetic).
import type { CmdResult, Command } from '../sim/commands.ts';
import type { SimDefs } from '../sim/defs.ts';
import type { SimEvent } from '../sim/events.ts';
import { Simulation } from '../sim/Simulation.ts';
import type { SimState } from '../sim/state.ts';
import { GameLoop, type LoopConfig } from './GameLoop.ts';
import type { SimObserver } from './observer.ts';
import type { ScenarioRunner } from './scenarios.ts';
import type { SnapshotBuilder, UiSnapshot } from './snapshot.ts';

export type { SimObserver } from './observer.ts';

/** A consumer of cosmetic events (renderer, audio). */
export interface EventSink {
  onEvents(events: readonly SimEvent[]): void;
}

/** The audio as the controller sees it (M9 `GameAudio`): `reset` forgets combos / limiters on a replace. */
export interface AudioEventSink extends EventSink {
  reset?(): void;
}

/** The DOM FX layer (M10 fx/DomFx.ts): cosmetic like the renderer; `reset` on a replace / silent rebuild. */
export interface FxEventSink extends EventSink {
  reset?(): void;
}

/** The renderer as the controller sees it. */
export interface ViewSink extends EventSink {
  frame(alpha: number): void;
  rebuildFromState(): void;
}

export interface ControllerDeps {
  defs: SimDefs;
  state: SimState;
  loop: LoopConfig;
  /** Initial game speed (0 = stopped: ?test). */
  speed: number;
  snapshotHz: number;
  snapshots: SnapshotBuilder;
  view: ViewSink;
  audio: AudioEventSink;
  /** M10: the DOM FX layer (coins, floaters, ribbons) — cosmetic, never on a silent advance. */
  fx?: FxEventSink;
  /** M11 autosave hook (§2.1): the same event batches as audio; the lifecycle debounces a save on them. */
  autosave?: EventSink;
  /** Receives every snapshot (the UI store). */
  publish(snap: UiSnapshot, nowMs: number): void;
  /** Keep a copy of every event for `__gm.events()` (hooks builds only), capped at this many. */
  gmEventsMax: number;
  runner?: ScenarioRunner | null;
  /** The board mask changed (`animate`: by an expansion, not by a load). */
  onBoard?(bounds: number, animate: boolean): void;
  /** A frame threw: the game is halted (called once). */
  onError?(err: unknown): void;
  /** Command-log cap of the live Simulation (ui.json debug.commandLogMax; unbounded when omitted). */
  logCap?: number;
  now(): number;
  /** M12: telemetry sink, trace recorder. */
  observers?: readonly SimObserver[];
}

export interface FanoutCounts {
  render: number;
  audio: number;
  gm: number;
}

export class GameController {
  private simNow: Simulation;
  private readonly d: ControllerDeps;
  private readonly loop: GameLoop;
  private runner: ScenarioRunner | null;
  private gm: SimEvent[] = [];
  private readonly counts: FanoutCounts = { render: 0, audio: 0, gm: 0 };
  private lastSnapAt = Number.NEGATIVE_INFINITY;
  private lastSnap: UiSnapshot | null = null;
  private bounds: number;
  private failed: unknown = null;

  constructor(deps: ControllerDeps) {
    this.d = deps;
    this.simNow = this.newSim(deps.defs, deps.state);
    this.runner = deps.runner ?? null;
    this.bounds = deps.state.bounds;
    this.loop = new GameLoop(deps.loop, { step: (silent) => this.step(silent) }, deps.speed);
    for (const o of deps.observers ?? []) o.onSim?.(this.simNow);
  }

  get sim(): Simulation {
    return this.simNow;
  }

  get speed(): number {
    return this.loop.speed;
  }

  /** True once a frame threw: nothing ticks, draws or saves any more. */
  get halted(): boolean {
    return this.failed !== null;
  }

  private newSim(defs: SimDefs, state: SimState): Simulation {
    return new Simulation(
      defs,
      state,
      this.d.logCap !== undefined ? { logCap: this.d.logCap } : {},
    );
  }

  /** Dispatch-time apply (§0.1) with same-frame fan-out. */
  dispatch(cmd: Command, silent = false): CmdResult {
    const sim = this.simNow;
    const cash = sim.state.wallet.cash;
    const r = sim.apply(cmd);
    const evs = sim.drainEvents();
    for (const o of this.d.observers ?? []) {
      o.onCommand?.(cmd, r, cash, evs, sim);
      o.onEvents?.(evs, sim);
    }
    this.fanOut(evs, silent);
    this.checkBoard(true);
    return r;
  }

  /** One tick: the scenario's commands due now, then the sim tick, then the fan-out. */
  step(silent: boolean): void {
    const sim = this.simNow;
    this.runner?.applyDue(
      sim.state.tick,
      () => sim.state,
      sim.defs,
      (cmd) => {
        this.dispatch(cmd, silent);
      },
    );
    sim.tick();
    const evs = sim.drainEvents();
    for (const o of this.d.observers ?? []) o.onEvents?.(evs, sim);
    this.fanOut(evs, silent);
    this.checkBoard(true);
  }

  /** One display frame: loop ticks, the 10 Hz snapshot, the renderer's view sync. */
  frame(nowMs: number): void {
    if (this.failed !== null) return;
    try {
      const alpha = this.loop.frame(nowMs);
      if (nowMs - this.lastSnapAt >= 1000 / this.d.snapshotHz) this.publish(nowMs);
      this.d.view.frame(alpha);
    } catch (err) {
      this.failed = err ?? new Error('frame failed');
      this.loop.setSpeed(0);
      this.d.onError?.(err);
    }
  }

  /**
   * Runs `n` ticks now; `silent` keeps cosmetic events from render / audio and rebuilds the views;
   * `publish` = false skips the UI snapshot publish (tick-only timing; the loop publishes at snapshotHz).
   */
  advance(n: number, silent = false, publish = true): void {
    this.loop.advance(n, silent);
    if (silent) {
      this.d.view.rebuildFromState();
      this.d.fx?.reset?.();
    }
    if (publish) this.publish(this.d.now());
  }

  setSpeed(k: number): void {
    this.loop.setSpeed(k);
  }

  /** Replaces the simulation (save load, `__gm.loadSim`, reset). Views and caches are rebuilt. */
  replace(defs: SimDefs, state: SimState, runner: ScenarioRunner | null = null): void {
    this.simNow = this.newSim(defs, state);
    this.runner = runner;
    for (const o of this.d.observers ?? []) o.onSim?.(this.simNow);
    this.d.snapshots.reset();
    this.d.audio.reset?.();
    this.d.view.rebuildFromState();
    this.d.fx?.reset?.();
    this.checkBoard(false);
    this.publish(this.d.now());
  }

  /** Page hidden (§0.1): end any drag and hold so a replay trace stays valid; the caller saves after. */
  onHidden(): void {
    this.dispatch({ c: 'release' });
    this.dispatch({ c: 'holdEnd' });
    this.loop.resetClock();
  }

  /**
   * Page visible again (M11: visibilitychange, a bfcache restore, Page Lifecycle resume): restart the loop
   * clock so the time away is neither replayed nor clamped into a burst.
   */
  onShown(): void {
    this.loop.resetClock();
  }

  /** Builds and publishes a snapshot now (the 10 Hz cadence otherwise). */
  publish(nowMs: number): UiSnapshot {
    const snap = this.d.snapshots.build(this.simNow.state, this.simNow.defs);
    this.lastSnap = snap;
    this.lastSnapAt = nowMs;
    this.d.publish(snap, nowMs);
    return snap;
  }

  /** The last published snapshot (building one if none exists yet). */
  snapshot(): UiSnapshot {
    return this.lastSnap ?? this.publish(this.d.now());
  }

  /** Events recorded for `__gm.events()` since the last call. */
  drainGmEvents(): SimEvent[] {
    const out = this.gm;
    this.gm = [];
    return out;
  }

  fanoutCounts(): FanoutCounts {
    return { ...this.counts };
  }

  private fanOut(evs: SimEvent[], silent: boolean): void {
    if (evs.length === 0) return;
    if (this.d.gmEventsMax > 0) {
      for (const e of evs) this.gm.push(e);
      if (this.gm.length > this.d.gmEventsMax)
        this.gm.splice(0, this.gm.length - this.d.gmEventsMax);
      this.counts.gm += evs.length;
    }
    if (silent) return;
    this.d.view.onEvents(evs);
    this.counts.render += evs.length;
    this.d.audio.onEvents(evs);
    this.counts.audio += evs.length;
    this.d.fx?.onEvents(evs);
    this.d.autosave?.onEvents(evs);
  }

  private checkBoard(animate: boolean): void {
    const b = this.simNow.state.bounds;
    if (b === this.bounds) return;
    this.bounds = b;
    this.d.onBoard?.(b, animate);
  }
}
